<template>
  <ms-dynamic-popup
    title="Danh sách chứng từ làm việc ngoại tuyến"
    @beforeOpen="beforeOpen($event, close)"
    @beforeClose="beforeClose"
    @shortkeyAction="shortkeyAction"
    :maximum="true"
    class="voucher-offline"
  >
    <template v-slot:icon>
      <div class="button icon24 ic-question mr-1" v-tooltip="'Trợ giúp'" shortkey-target="Help"></div>
    </template>
    <template v-slot:content>
      <div class="pd-popup mt-2">
        <ms-grid-viewer
          ref="viewRef"
          :data="items"
          :pageTotal="items.length"
          :columns="columnData"
          :loading="false"
          class="flex-box"
          idField="ref_id"
          :filterable="false"
          v-model:selected="gridInfo.selected"
          :pageSize="gridInfo.pageSize"
          v-on="gridEvent"
          queryMode="local"
          :footer="false"
          pagination
          @viewLinkedForm="
            (col, dataRow) => {
              viewLinkedForm(col, dataRow.Model);
            }
          "
        >
          <template #widget-title-th="{rowspan}">
            <th class="ms-th widget-title" :rowspan="rowspan" style="width: 105px; min-width: 105px">Chức năng</th>
          </template>
          <template #widget-body="{record}">
            <div class="widget-container">
              <div class="widget-primary" @click="gridRowActionClick(grid, $ms.constant.Command.View, record)">
                Xem/Sửa
              </div>
            </div>
          </template>
        </ms-grid-viewer>
      </div>
    </template>
  </ms-dynamic-popup>
</template>

<script>
import {defineComponent, onMounted, getCurrentInstance, ref, reactive} from 'vue';
import baseReportParam from '@/views/base/baseReportParam';
import msDynamicPopup from '@/components/popup/MsDynamicPopup.vue';
import MsGridViewer from '@/components/gridViewer/MsGridViewer.vue';
import popupUtil from '@/commons/popupUtil';
import {useActionCommonDetail} from '../../commons/popupList/actionCommonDetail.js';
import {QueueStore} from 'workbox-background-sync';
import indexedDB from '@/commons/indexedDB';

export default defineComponent({
  components: {msDynamicPopup, MsGridViewer},
  extends: baseReportParam,

  setup() {
    const {proxy} = getCurrentInstance();

    const {gridInfo, gridEvent} = useActionCommonDetail(proxy);
    const items = ref([]);

    const columnData = reactive([
      {
        caption: 'Ngày HT',
        title: 'Ngày hạch toán',
        dataField: 'posted_date',
        width: 150,
        filterable: false,
        formatType: proxy.$ms.constant.FormatType.DateTime,
        formatString: 'DD/MM/YYYY',
      },
      {
        caption: 'Số CT',
        title: 'Số chứng từ',
        dataField: 'ref_no',
        width: 150,
        filterable: false,
        linkedForm: 'BUTransferDepositDetail',
      },
      {
        caption: 'Ngày CT',
        title: 'Ngày chứng từ',
        dataField: 'ref_date',
        width: 150,
        filterable: false,
        formatType: proxy.$ms.constant.FormatType.DateTime,
        formatString: 'DD/MM/YYYY',
      },
      {
        caption: 'Diễn giải',
        title: 'Diễn giải',
        dataField: 'description',
        filterable: false,
      },
      {
        caption: 'Số tiền',
        title: 'Số tiền',
        dataField: 'amount',
        filterable: false,
        formatType: proxy.$ms.constant.FormatType.Amount,
      },
      {
        caption: 'Loại chứng từ',
        title: 'Loại chứng từ',
        dataField: 'ref_type_name',
        filterable: false,
      },
    ]);

    /**
     * Override sự kiện khi đóng form
     */
    function beforeClose() {
      popupUtil.hideAll();
    }

    /**
     * Xử lý mở form để lấy dữ liệu từ queue cache
     */
    async function bindData(data) {
      const indexDb = new indexedDB();
      let dataQueue = await indexDb.get('cache_dataOffline');
      items.value = genDataFromQueue(dataQueue);
    }

    /**
     * Biến đổi dữ liệu thành dạng list từ dữ liệu queue
     */
    function genDataFromQueue(dataQueue) {
      var dataList = [];
      if (dataQueue.length > 0) {
        dataQueue.forEach(item => {
          switch (item.ref_type) {
            default:
              let dataMapping = {
                ref_type: item.ref_type,
                ref_no: item?.ref_no,
                ref_id: proxy.$ms.commonFn.newGuid(),
                ref_date: item?.ref_date,
                posted_date: item?.posted_date,
                description: item?.journal_memo,
                amount: item?.total_amount,
                ref_type_name: item.ref_type == 401 ? 'Chứng từ nghiệp vụ khác' : item.ref_type_name,
                Model: item,
              };
              dataList.push(dataMapping);
              break;
          }
        });
      }

      return dataList;
    }

    /**
     * override để custom tên form theo đặc thù từng bản ghi
     * @param {*} dataRow dòng dữ liệu được click
     */
    function getLinkedFormCustom(dataRow) {
      switch (dataRow.ref_type) {
        case proxy.$ms.constant.RefType.BUPlanWithdrawCash: // Rút dự toán tiền mặt
          return 'BUPlanWithdrawCashDetail';
        case proxy.$ms.constant.RefType.BUPlanWithdrawDeposit: // Rút dự toán tiền gửi
          return 'BUPlanWithdrawDepositDetail';
        case proxy.$ms.constant.RefType.BUTransferDeposit: // Chuyển khoản kho bạc vào TK tiền gửi
          return 'BUTransferDepositDetail';
        case proxy.$ms.constant.RefType.BAWithDraw: // Chi tiền gửi
          return 'BAWithDrawDetail';
        case proxy.$ms.constant.RefType.CAReceipt: // Phiếu thu
          return 'CAReceiptDetail';
        case proxy.$ms.constant.RefType.CAPayment: // Phiếu chi
          return 'CAPaymentDetail';
        case proxy.$ms.constant.RefType.GLGeneralVoucher: // Chứng từ nghiệp vụ khác
          return 'GLGeneralVoucherDetail';
      }
    }

    /**
     * Lấy data queue
     */
    async function getQueueData() {
      const queue = new QueueStore('myQueueName');
      var allQueueData = await queue.getAll();

      var queueData = allQueueData.map(x => JSON.parse(new TextDecoder().decode(x.requestData.body)));

      return queueData.map(x => x.Model);
    }

    /**
     * Override lại hàm xem sửa
     */
    function gridRowActionClick(grid, command, record, event) {
      const me = this;
      switch (command) {
        case me.$ms.constant.Command.Edit:
        case me.$ms.constant.Command.View:
          let formParam = getLinkedFormCustom(record.Model);
          popupUtil.show(formParam, {
            mode: proxy.$ms.constant.FormState.Edit,
            data: record.Model,
            options: {
              close: async () => {
                await bindData();
              },
            },
          });
          break;
      }
    }
    /**
     * override custom param khi click link trên grid
     */
    function customParameterIncurredData(params) {
      if (params.options) {
        params.options = {
          ...params.options,
          close: async () => {
            await bindData();
          },
        };
      }
    }
    return {
      gridInfo,
      gridEvent,
      items,
      columnData,
      beforeClose,
      bindData,
      getQueueData,
      genDataFromQueue,
      getLinkedFormCustom,
      gridRowActionClick,
      customParameterIncurredData,
    };
  },
});
</script>

<style lang="scss">
.voucher-offline {
  .ms-th.widget-title {
    right: 0;
    z-index: 3;
    border-left: 1px solid $ms-th-grid-border-color !important;
    border-right: none !important;
  }
}
</style>
